import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { AccountMappingDTO } from '@/shared/api/model';
import { 
  useGetAllAccountMappingsByProductPropertiesGlNo, 
  useCreateAccountMapping,
  useUpdateAccountMapping,
  useDeleteAccountMapping
} from '@/shared/api/client/account-mapping-resource';

export interface UseAccountMappingsProps {
  productGlNo?: string;
}

export const useAccountMappings = ({ productGlNo }: UseAccountMappingsProps) => {
  const [accountMappings, setAccountMappings] = useState<AccountMappingDTO[]>([]);
  const queryClient = useQueryClient();
  
  // Fetch account mappings for the product
  const { 
    data: mappingsData, 
    isLoading: isLoadingMappings, 
    refetch: refetchMappings 
  } = useGetAllAccountMappingsByProductPropertiesGlNo(productGlNo || '', {
    query: {
      enabled: !!productGlNo
    }
  });
  
  // Mutation hooks
  const { mutateAsync: createMappingMutation } = useCreateAccountMapping();
  const { mutateAsync: updateMappingMutation } = useUpdateAccountMapping();
  const { mutateAsync: deleteMappingMutation } = useDeleteAccountMapping();

  // Update mappings when data is loaded
  useEffect(() => {
    if (mappingsData) {
      setAccountMappings(mappingsData);
    } else {
      setAccountMappings([]);
    }
  }, [mappingsData]);

  // Handle account mappings change
  const handleAccountMappingsChange = useCallback((updatedMappings: AccountMappingDTO[]) => {
    setAccountMappings(updatedMappings);
  }, []);

  // Save a single account mapping
  const saveAccountMapping = useCallback(async (mapping: AccountMappingDTO) => {
    try {
      if (!productGlNo) {
        throw new Error('Product GL number is required');
      }

      const preparedMapping = {
        ...mapping,
        productPropertiesGlNo: productGlNo
      };

      let savedMapping: AccountMappingDTO;
      
      if (mapping.id) {
        // Update existing mapping
        const response = await updateMappingMutation({ id: mapping.id, data: preparedMapping });
        savedMapping = response;
      } else {
        // Create new mapping
        const response = await createMappingMutation({ data: preparedMapping });
        savedMapping = response;
      }

      // Update local state
      setAccountMappings(prevMappings => {
        const index = prevMappings.findIndex(m => 
          m.id === savedMapping.id || m.mappingType === savedMapping.mappingType
        );
        
        if (index >= 0) {
          // Replace existing mapping
          const newMappings = [...prevMappings];
          newMappings[index] = savedMapping;
          return newMappings;
        } else {
          // Add new mapping
          return [...prevMappings, savedMapping];
        }
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: [`/api/account-mappings/product-properties/${productGlNo}`] });
      
      return savedMapping;
    } catch (error) {
      console.error('Error saving account mapping:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save account mapping'
      });
      throw error;
    }
  }, [productGlNo, createMappingMutation, updateMappingMutation, queryClient]);

  // Delete an account mapping
  const deleteAccountMapping = useCallback(async (mappingId: number) => {
    try {
      await deleteMappingMutation({ id: mappingId });
      
      // Update local state
      setAccountMappings(prevMappings => prevMappings.filter(m => m.id !== mappingId));
      
      // Invalidate queries
      if (productGlNo) {
        queryClient.invalidateQueries({ queryKey: [`/api/account-mappings/product-properties/${productGlNo}`] });
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting account mapping:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to delete account mapping'
      });
      return false;
    }
  }, [deleteMappingMutation, productGlNo, queryClient]);

  // Save all account mappings
  const saveAllAccountMappings = useCallback(async () => {
    try {
      if (!productGlNo) {
        throw new Error('Product GL number is required');
      }

      const savedMappings = [];
      
      // Process each mapping
      for (const mapping of accountMappings) {
        const savedMapping = await saveAccountMapping(mapping);
        savedMappings.push(savedMapping);
      }
      
      return savedMappings;
    } catch (error) {
      console.error('Error saving account mappings:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save account mappings'
      });
      return accountMappings;
    }
  }, [accountMappings, productGlNo, saveAccountMapping]);

  return {
    accountMappings,
    isLoading: isLoadingMappings,
    handleAccountMappingsChange,
    saveAccountMapping,
    deleteAccountMapping,
    saveAllAccountMappings,
    refetchMappings
  };
};

export default useAccountMappings;
